import { ButtonSmallSecondary } from "@studio3t/generic-components";
import { useCallback, useRef, useState } from "react";
import {
  FormGroup,
  HelpText,
  HiddenFileInput,
  Label,
  PanelTextarea,
  UploadFileName,
  UploadPanel,
  UploadPanelDivider,
  UploadPanelRow,
  UploadRow,
} from "./styles";

interface PemFieldProps {
  label: string;
  id: string;
  value: string;
  onChange: (value: string) => void;
  accept: string;
  ariaLabel: string;
  placeholder: string;
  helpText: string;
  required?: boolean;
}

// PEM upload field in the shared upload-panel style (same treatment as the
// Pub/Sub service account key): upload button + chosen-file label, then a
// paste textarea with the supportive text below.
export function PemField({
  label,
  id,
  value,
  onChange,
  accept,
  ariaLabel,
  placeholder,
  helpText,
  required,
}: PemFieldProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Presentational only: which file filled the textarea. Cleared on manual
  // edits, since the content no longer matches the chosen file.
  const [fileName, setFileName] = useState<string | null>(null);

  const handleFileUpload = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      setFileName(file.name);
      const reader = new FileReader();
      reader.onload = () => {
        if (typeof reader.result === "string") {
          onChange(reader.result);
        }
      };
      reader.readAsText(file);
      e.currentTarget.value = "";
    },
    [onChange],
  );

  return (
    <FormGroup>
      <Label htmlFor={id}>{label}</Label>
      <UploadPanel>
        <UploadPanelRow>
          <HiddenFileInput
            ref={fileInputRef}
            type="file"
            accept={accept}
            aria-label={ariaLabel}
            onChange={handleFileUpload}
          />
          <UploadRow>
            <ButtonSmallSecondary
              // GenericButton renders a <button> without a type attribute,
              // which defaults to "submit" inside the form — clicking it
              // would fire native validation and, on a valid form, submit it.
              ref={(el) => el?.setAttribute("type", "button")}
              text="Upload a .pem file"
              onClick={() => fileInputRef.current?.click()}
            />
            <UploadFileName>{fileName ?? "No file chosen"}</UploadFileName>
          </UploadRow>
        </UploadPanelRow>

        <UploadPanelDivider />

        <UploadPanelRow>
          <PanelTextarea
            id={id}
            value={value}
            onChange={(e) => {
              onChange(e.target.value);
              setFileName(null);
            }}
            placeholder={placeholder}
            required={required}
          />
          <HelpText>{helpText}</HelpText>
        </UploadPanelRow>
      </UploadPanel>
    </FormGroup>
  );
}
